import React, { useState, useMemo } from "react";
import { TabHeader } from "components/TabHeader";
import { Button } from "components/RFQ/Button";
import { Icon } from "assets/icons/Icon";
import { Flex } from "@chakra-ui/react";
import { navigate } from "@reach/router";
import { useSelector } from "react-redux";
import {
	useTemplates,
	createTemplate,
	deleteRFXTemplate,
	updateRFXTemplateStatus,
} from "../../../actions/RFQ/templateAction";
import { formattedSimpleDate } from "utils/dateFormat";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "components/RFQ/Table";
import {
	TemplateActionsButton,
	DeleteConfirmationModal,
	TemplateStatusTag,
} from "./_components";
import { Pagination } from "components/Pagination";

export const ManageTemplates = () => {
	const { current_company } = useSelector((state: any) => state.auth);
	const companyId = current_company?.id;
	const [currentPage, setCurrentPage] = useState(1);
	const [deleteModal, setDeleteModal] = useState<{
		isOpen: boolean;
		templateId?: number;
		templateName?: string;
	}>({ isOpen: false });

	const {
		data: templatesResponse,
		isLoading,
		error,
		refetch,
	} = useTemplates(currentPage);

	// Extract templates and pagination info with type guard
	const templates = Array.isArray(templatesResponse)
		? templatesResponse
		: templatesResponse?.data || [];
	const meta = Array.isArray(templatesResponse)
		? undefined
		: templatesResponse?.meta;

	// to avoid unnecessary re-renders
	const totalTemplates = useMemo(() => meta?.total || 0, [meta?.total]);

	const handleCreateTemplate = async () => {
		try {
			const response = await createTemplate(companyId);
			if (response?.id) {
				navigate(`/account/${companyId}/create-rfx-template/${response.id}`);
			}
		} catch (error) {
			console.error("Template creation error:", error);
		}
	};

	const handleEditTemplate = (templateId: number) => {
		navigate(`/account/${companyId}/edit-rfx-template/${templateId}`);
	};

	const handleLockTemplate = async (
		templateId: number,
		currentStatus: string
	) => {
		const newStatus = currentStatus === "locked" ? "active" : "locked";
		try {
			await updateRFXTemplateStatus(templateId, newStatus);
			refetch();
		} catch (error) {
			console.error("Template lock/unlock error:", error);
		}
	};

	const handleArchiveTemplate = async (
		templateId: number,
		currentStatus: string
	) => {
		const newStatus = currentStatus === "archived" ? "active" : "archived";
		try {
			await updateRFXTemplateStatus(templateId, newStatus);
			refetch();
		} catch (error) {
			console.error("Template archive/unarchive error:", error);
		}
	};

	const handleDeleteTemplate = (templateId: number, templateName?: string) => {
		setDeleteModal({
			isOpen: true,
			templateId,
			templateName,
		});
	};

	const confirmDeleteTemplate = async () => {
		if (!deleteModal.templateId) return;

		try {
			await deleteRFXTemplate(deleteModal.templateId);
			refetch();
		} catch (error) {
			console.error("Template deletion error:", error);
		}
	};

	const closeDeleteModal = () => {
		setDeleteModal({ isOpen: false });
	};

	const handlePageChange = (newPage: number) => {
		setCurrentPage(newPage);
	};

	const formatDate = (dateString?: string) => {
		if (!dateString) return "-";
		return formattedSimpleDate(dateString);
	};

	if (error) {
		return (
			<div className="my-8 p-6">
				<div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
					Error loading templates. Please try again.
				</div>
			</div>
		);
	}

	return (
		<div className="overflow-visible">
			<TabHeader heading="Manage Templates">
				<div className="flex gap-6">
					<Button
						btntype="primary"
						className="!h-[50px]"
						onClick={handleCreateTemplate}
					>
						<Flex className="items-center gap-[6px]">
							<div className="flex h-5 w-5 items-center justify-center">
								<Icon
									icon="plus"
									style={{
										width: "12px",
										height: "12px",
										color: "#fff",
										fontWeight: "bold",
									}}
								/>
							</div>
							<div className="font-roboto text-base font-semibold">
								Create Template
							</div>
						</Flex>
					</Button>
				</div>
			</TabHeader>

			{/* Status Summary */}
			<div className="w-full space-y-4 rounded-2xl bg-blue-50 p-4">
				<h3 className="text-md mb-2 font-semibold text-gray-700">
					Template Status Guide
				</h3>
				<div className="space-y-3 text-xs text-gray-600">
					<div className="flex items-center gap-3">
						<div className="w-16">
							<TemplateStatusTag status="active" />
						</div>
						<span>Available for creating RFx and all actions allowed.</span>
					</div>
					<div className="flex items-center gap-3">
						<div className="w-16">
							<TemplateStatusTag status="locked" />
						</div>
						<span>
							Protected from deletion and modification, available for RFx
							creation.
						</span>
					</div>
					<div className="flex items-center gap-3">
						<div className="w-16">
							<TemplateStatusTag status="archived" />
						</div>
						<span>Hidden from RFx creation, visible only in management.</span>
					</div>
				</div>
			</div>
			<div className="my-8 rounded-2xl shadow-[0px_0px_10px_-4px_rgba(0,0,0,0.25)]">
				{/* Header */}
				<div className="w-full border-t border-gray-200 p-6">
					<h2 className="min-h-[46px] text-lg font-semibold leading-[46px] text-gray-800">
						My Templates ({totalTemplates})
					</h2>
				</div>

				{/* Table */}
				<Table
					containerClass="w-full rounded-2xl rounded-t-none border-t border-gray-200"
					className=""
				>
					<TableHeader className="">
						<TableRow className="">
							<TableHead
								firstCol={true}
								className="!pl-6"
							>
								<div
									className="flex"
									style={{ gap: "8px", alignItems: "center" }}
								>
									Template Name
								</div>
							</TableHead>
							<TableHead className="">Created Date</TableHead>
							<TableHead className="">Last Modified</TableHead>
							<TableHead className="[&_*]:text-center">Status</TableHead>
							<TableHead className="max-w-[50px] [&>div]:text-right">
								Actions
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody className="">
						{isLoading ? (
							[...Array(4)].map((_, index) => (
								<TableRow
									key={index}
									className=""
								>
									<TableCell
										firstCol={true}
										className="!pl-6"
									>
										<div className="h-6 w-full animate-pulse bg-gray-300" />
									</TableCell>
									<TableCell className="">
										<div className="h-6 w-full animate-pulse bg-gray-300" />
									</TableCell>
									<TableCell className="">
										<div className="h-6 w-full animate-pulse bg-gray-300" />
									</TableCell>
									<TableCell className="">
										<div className="h-6 w-full animate-pulse bg-gray-300" />
									</TableCell>
									<TableCell className="max-w-[50px]">
										<div className="h-6 w-full animate-pulse bg-gray-300" />
									</TableCell>
								</TableRow>
							))
						) : templates.length === 0 ? (
							<TableRow className="">
								<TableCell
									className="py-8"
									style={{ gridColumn: "1 / -1" }}
								>
									<div className="text-gray-500">
										No templates found. Create your first template to get
										started.
									</div>
								</TableCell>
							</TableRow>
						) : (
							templates.map((template) => (
								<TableRow
									key={template.id}
									className="group transition-colors hover:bg-utility-blue-light-50/30"
								>
									<TableCell
										firstCol={true}
										className="w-[40%] overflow-hidden !pl-6 group-hover:bg-utility-blue-light-50/30"
									>
										<div className="flex items-center gap-3">
											<div className="flex h-10 w-10 items-center justify-center rounded-lg bg-utility-blue-light-100">
												<Icon
													icon="template"
													style={{
														width: "20px",
														height: "20px",
														color: "#0031DD",
													}}
												/>
											</div>
											<div>
												<p className="font-roboto font-medium text-gray-900">
													{template.name ||
														template.template_name ||
														`Template ${template.id}`}
												</p>
											</div>
										</div>
									</TableCell>
									<TableCell className="group-hover:bg-utility-blue-light-50/30">
										<p className="font-roboto text-sm text-gray-600">
											{formatDate(template.created_at)}
										</p>
									</TableCell>
									<TableCell className="group-hover:bg-utility-blue-light-50/30">
										<p className="font-roboto text-sm text-gray-600">
											{formatDate(template.last_modified_at)}
										</p>
									</TableCell>
									<TableCell className="text-center group-hover:bg-utility-blue-light-50/30">
										<TemplateStatusTag status={template.status || "active"} />
									</TableCell>
									<TableCell className="group-hover:bg-utility-blue-light-50/30">
										<TemplateActionsButton
											templateId={template.id}
											template={template}
											onEdit={() => handleEditTemplate(template.id)}
											onLock={() =>
												handleLockTemplate(
													template.id,
													template.status || "active"
												)
											}
											onArchive={() =>
												handleArchiveTemplate(
													template.id,
													template.status || "active"
												)
											}
											onDelete={() =>
												handleDeleteTemplate(
													template.id,
													template.name ||
														template.template_name ||
														`Template ${template.id}`
												)
											}
										/>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>
			{/* Pagination */}
			{meta && (
				<Pagination
					currentPage={meta.current_page}
					totalItems={meta.total}
					itemsPerPage={meta.per_page}
					onPageChange={handlePageChange}
					showTotal={true}
					className="my-8"
				/>
			)}

			{/* Delete Confirmation Modal */}
			<DeleteConfirmationModal
				isOpen={deleteModal.isOpen}
				onClose={closeDeleteModal}
				onConfirm={confirmDeleteTemplate}
				templateName={deleteModal.templateName}
			/>
		</div>
	);
};
